import { useEffect, useState } from 'react';
import { ConsentInviteError, getConsentInviteErrorMessage, unwrapCortexResponse } from './consent_invite.helpers';
/**
 * Validates a decoded consent invite against Cortex and resolves the authenticated
 * Studio account behind it. Runs once `inviteContext` and auth state are known;
 * requires the signed-in user's email to match the invite's `ec__email` before
 * ever hitting the network, since a mismatched account can never complete this
 * invite regardless of what Cortex says.
 */
export function useConsentInviteAccount({ inviteContext, isAuthLoading, isLoggedIn, userEmail, translate, }) {
    const [account, setAccount] = useState();
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        if (!inviteContext) {
            setError(translate('This consent invitation is invalid or incomplete.'));
            setIsLoading(false);
            return;
        }
        if (isAuthLoading || !isLoggedIn) {
            return;
        }
        if (!userEmail || userEmail.toLowerCase() !== inviteContext.invite.ec__email.toLowerCase()) {
            setError(translate('Sign in as {email} to open this consent invitation.', { email: inviteContext.invite.ec__email }));
            setIsLoading(false);
            return;
        }
        let isActive = true;
        setError('');
        setIsLoading(true);
        Promise.all([
            inviteContext.api.get(`/routes/econsent/validate_invite/${inviteContext.token}`),
            inviteContext.accountApi.get('/v2/hub/accounts/login'),
        ])
            .then(([validationResponse, accountResponse]) => {
            if (!isActive) {
                return;
            }
            const validation = unwrapCortexResponse(validationResponse);
            if (typeof validation?.ec__requires_registration !== 'boolean') {
                throw new ConsentInviteError(translate('The consent invitation response was incomplete.'));
            }
            const axonAccount = unwrapCortexResponse(accountResponse);
            if (!axonAccount?._id) {
                throw new ConsentInviteError(translate('The account response was incomplete.'));
            }
            if (axonAccount.email && axonAccount.email.toLowerCase() !== inviteContext.invite.ec__email.toLowerCase()) {
                throw new ConsentInviteError(translate('The authenticated account does not match this consent invitation.'));
            }
            setAccount({
                _id: axonAccount._id,
                email: axonAccount.email || inviteContext.invite.ec__email,
                locale: 'en_US',
                name: {
                    first: axonAccount.name?.first || '',
                    last: axonAccount.name?.last || '',
                },
            });
        })
            .catch((validationError) => {
            if (isActive) {
                setError(getConsentInviteErrorMessage(validationError, translate));
            }
        })
            .finally(() => {
            if (isActive) {
                setIsLoading(false);
            }
        });
        return () => {
            isActive = false;
        };
    }, [inviteContext, isAuthLoading, isLoggedIn, userEmail, translate]);
    return { account, error, setError, isLoading };
}
